import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppContext } from '../../context/AppContext';
import SessionExpiryWarning from '../../components/SessionExpiryWarning';  
import Sidebar from './Sidebar/Sidebar';
import Topbar from './Topbar/Topbar';
import Footer from './Footer/Footer';
import './layout.css';

const MainLayout = () => {
  const { user, isAuthenticated, loading, initialized, logout, isSystemAdmin, hasRole } = useAuth();
  const { appSettings } = useAppContext(); 
  const navigate = useNavigate();
  const location = useLocation();
  
  // Start with sidebar closed on mobile, open on desktop
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 992);
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Apply sidebar state as CSS variables
  useEffect(() => {
    const sidebarWidth = isMobile ? 
      (isSidebarOpen ? '280px' : '0') : 
      (isSidebarCollapsed ? '70px' : '280px');
    
    document.documentElement.style.setProperty('--sidebar-width', sidebarWidth);
    document.documentElement.style.setProperty('--sidebar-collapsed-width', isSidebarCollapsed ? '70px' : '280px');
  }, [isSidebarOpen, isSidebarCollapsed, isMobile]);

  // Check if device is mobile and handle resize - SIMPLIFIED
  useEffect(() => {
    const handleResize = () => {
      const mobileCheck = window.innerWidth < 992;
      setIsMobile(mobileCheck);
      
      // Only auto-close on mobile resize, don't auto-open
      if (mobileCheck && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSidebarOpen]);

  // Close sidebar on route change only if on mobile
  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isMobile, isSidebarOpen]);

  // CRITICAL FIX: Moved useEffect for redirect outside conditional render
  useEffect(() => {
    // Only navigate if authentication is fully initialized AND user is NOT authenticated AND NOT in a loading state
    if (initialized && !loading && !isAuthenticated) {
      const timer = setTimeout(() => {
        navigate('/login', { replace: true });
      }, 100); 
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, loading, initialized, navigate]);

  // NEW: Toggle sidebar collapse (for desktop)
  const toggleSidebarCollapse = useCallback(() => {
    console.log('Toggle collapse - current:', isSidebarCollapsed);
    setIsSidebarCollapsed(prev => !prev);
  }, [isSidebarCollapsed]);

  // NEW: Toggle sidebar open/close (for mobile) - SIMPLIFIED
  const toggleSidebar = useCallback(() => {
    console.log('Toggle sidebar - isMobile:', isMobile, 'current open:', isSidebarOpen);
    
    if (isMobile) {
      setIsSidebarOpen(prev => {
        console.log('Setting mobile sidebar to:', !prev);
        return !prev;
      });
    } else {
      console.log('Toggling desktop collapse');
      toggleSidebarCollapse();
    }
  }, [isMobile, isSidebarOpen, toggleSidebarCollapse]);

  // Close sidebar specifically
  const closeSidebar = useCallback(() => {
    if (isMobile && isSidebarOpen) {
      console.log('Closing sidebar');
      setIsSidebarOpen(false);
    }
  }, [isMobile, isSidebarOpen]);

  // Calculate content area margin based on sidebar state - FIXED LOGIC
  const getContentMargin = () => {
    if (isMobile) {
      return isSidebarOpen ? '280px' : '0';
    }
    // On desktop, sidebar is always "open" but can be collapsed
    return isSidebarCollapsed ? '70px' : '280px';
  };

  // Debug effect
  useEffect(() => {
    console.log('Current state:', {
      isMobile,
      isSidebarOpen,
      isSidebarCollapsed,
      margin: getContentMargin()
    });
  }, [isMobile, isSidebarOpen, isSidebarCollapsed]);

  // --- Consolidated Loading and Authentication States ---

  // 1. Initial Application Initialization
  if (!initialized) {
    return (
      <div className="fullscreen-loading">
        <div className="text-center">
          <div className="spinner mb-4 w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-lg">Initializing application...</p>
        </div>
      </div>
    );
  }

  // 2. User Data Loading
  if (loading || !user) { 
    return (
      <div className="fullscreen-loading">
        <div className="text-center">
          <div className="spinner mb-4 w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-lg">Loading user data...</p> 
        </div>
      </div>
    );
  }
  
  // --- Normal Layout Rendering ---
  return (
    <div className="main-layout-container">

      {/* Session Expiry Warning - Shows modal when session is about to expire */}
      <SessionExpiryWarning />
      
      <Sidebar
        isOpen={isMobile ? isSidebarOpen : true} // On desktop, sidebar is always "open" but can be collapsed
        isCollapsed={!isMobile && isSidebarCollapsed} // Only collapsed on desktop
        toggleSidebar={toggleSidebar}
        toggleCollapse={toggleSidebarCollapse}
        user={user} 
        isMobile={isMobile}
      />

      <div 
        className="content-area"
        style={{
          marginLeft: getContentMargin(),
          transition: 'margin-left 0.3s ease'
        }}
      >
        <Topbar
          user={user}
          onMenuClick={toggleSidebar}
          logout={logout}
          isSystemAdmin={isSystemAdmin}
          hasRole={hasRole}
          isMobile={isMobile}
        />

        <main className="main-content">
          <Outlet context={{ user }} />
        </main>

        <Footer />
      </div>
      
      {/* Mobile overlay to close sidebar when clicking on content */}
      {isMobile && isSidebarOpen && (
        <div 
          className="sidebar-overlay active"
          onClick={closeSidebar}
        />
      )}
    </div>
  );
};

export default MainLayout;